export const fontFamilies = {
  body: "'Roboto', sans-serif",
  title: "'Open Sans', sans-serif",
  mono: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const lineHeights = {
  display: 1.167,
  displayTight: 1.1,
  heading: 1.2,
  headingComfortable: 1.235,
  title: 1.25,
  label: 1.3,
  body: 1.5,
  bodyComfortable: 1.45,
  bodyLoose: 1.75,
  compact: 1.43,
  caption: 1.35,
  captionLoose: 1.66,
  overline: 2.66,
} as const;

export const letterSpacing = {
  tightest: '-0.01562em',
  tighter: '-0.01em',
  tight: '-0.00833em',
  normal: '0em',
  body: '0.00938em',
  bodySmall: '0.01071em',
  title: '0.00735em',
  subtitle: '0.00714em',
  label: '0.0075em',
  button: '0.02857em',
  caption: '0.03333em',
  eyebrow: '0.08em',
  analyticsEyebrow: '0.24em',
} as const;

export const fontSizes = {
  h1: '6rem',
  h2: '3.75rem',
  h3: '3rem',
  h4: '2.125rem',
  h5: '1.5rem',
  h6: '1.25rem',

  subtitle1: '1rem',
  subtitle2: '0.875rem',
  body1: '1rem',
  body2: '0.875rem',
  button: '1rem',
  caption: '0.75rem',
  overline: '0.75rem',

  analyticsSectionTitle: '1.125rem',
  analyticsSectionDescription: '0.875rem',
  analyticsCardEyebrow: '10.5px',
  analyticsCardTitle: '15px',
  analyticsCardBody: '12.5px',
  analyticsPopoverTitle: '14px',
  analyticsPopoverBody: '12px',
  analyticsBigNumber: '44px',

  tableHeader: '14px',
  tableCell: '12.5px',
  chipLabel: '12px',
  filterLabel: '20px',
} as const;

export const typographyColors = {
  title: '#111111',
  primary: '#202124',
  secondary: 'rgba(0, 0, 0, 0.72)',
  muted: '#5F6368',
  helper: '#6B7280',
  inverse: '#FFFFFF',
} as const;

export const typography = {
  fontFamily: fontFamilies.body,

  h1: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.h1,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.display,
    letterSpacing: letterSpacing.tightest,
  },
  h2: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.h2,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.heading,
    letterSpacing: letterSpacing.tight,
  },
  h3: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.h3,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.display,
    letterSpacing: letterSpacing.normal,
  },
  h4: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.h4,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.headingComfortable,
    letterSpacing: letterSpacing.title,
  },
  h5: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.h5,
    fontWeight: fontWeights.regular,
    lineHeight: 1.334,
    letterSpacing: letterSpacing.normal,
  },
  h6: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.h6,
    fontWeight: fontWeights.medium,
    lineHeight: 1.6,
    letterSpacing: letterSpacing.label,
  },
  subtitle1: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.subtitle1,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.bodyLoose,
    letterSpacing: letterSpacing.body,
  },
  subtitle2: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.subtitle2,
    fontWeight: fontWeights.medium,
    lineHeight: 1.57,
    letterSpacing: letterSpacing.subtitle,
  },
  body1: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.body1,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.body,
    letterSpacing: letterSpacing.body,
  },
  body2: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.body2,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.compact,
    letterSpacing: letterSpacing.bodySmall,
  },
  button: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.button,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.bodyLoose,
    letterSpacing: letterSpacing.button,
    textTransform: 'capitalize' as const,
  },
  caption: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.caption,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.captionLoose,
    letterSpacing: letterSpacing.caption,
  },
  overline: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.overline,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.overline,
    letterSpacing: letterSpacing.eyebrow,
    textTransform: 'uppercase' as const,
  },
} as const;

export const semanticTypography = {
  pageTitle: {
    ...typography.h4,
    color: typographyColors.title,
  },
  sectionTitle: {
    ...typography.h5,
    color: typographyColors.title,
  },
  subsectionTitle: {
    ...typography.h6,
    color: typographyColors.title,
  },
  fieldLabel: {
    ...typography.subtitle1,
    fontWeight: fontWeights.semibold,
    color: typographyColors.title,
  },
  inputText: {
    ...typography.body1,
    color: typographyColors.primary,
  },
  helperText: {
    ...typography.body2,
    color: typographyColors.helper,
  },
  modalTitle: {
    ...typography.h5,
    fontWeight: fontWeights.bold,
    color: typographyColors.title,
  },
  modalBody: {
    ...typography.body1,
    lineHeight: 1.6,
    color: typographyColors.primary,
  },
  tableHeader: {
    ...typography.subtitle2,
    fontSize: fontSizes.tableHeader,
    fontWeight: fontWeights.bold,
    color: typographyColors.title,
  },
  tableCell: {
    ...typography.body2,
    fontSize: fontSizes.tableCell,
    lineHeight: 1.35,
    color: typographyColors.primary,
  },
  chipLabel: {
    ...typography.caption,
    fontSize: fontSizes.chipLabel,
    fontWeight: fontWeights.bold,
    lineHeight: 1.2,
  },
  filterSectionLabel: {
    ...typography.subtitle1,
    fontSize: fontSizes.filterLabel,
    fontWeight: fontWeights.semibold,
    lineHeight: lineHeights.label,
    color: typographyColors.title,
  },
} as const;

export const typographyTokens = {
  eyebrowText: {
    fontSize: fontSizes.chipLabel,
    lineHeight: '16px',
    fontWeight: fontWeights.regular,
    textTransform: 'uppercase',
    letterSpacing: letterSpacing.analyticsEyebrow,
  },
} as const;

export const analyticsTypography = {
  titleFontFamily: fontFamilies.title,
  bodyFontFamily: fontFamilies.body,

  sectionTitle: {
    fontFamily: fontFamilies.title,
    fontSize: fontSizes.analyticsSectionTitle,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.title,
    letterSpacing: '0.01em',
    textTransform: 'none' as const,
    color: typographyColors.title,
  },
  sectionDescription: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.analyticsSectionDescription,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.bodyComfortable,
    color: typographyColors.secondary,
  },
  cardEyebrow: {
    fontFamily: fontFamilies.title,
    fontSize: fontSizes.analyticsCardEyebrow,
    fontWeight: fontWeights.semibold,
    lineHeight: 1.2,
    letterSpacing: letterSpacing.eyebrow,
    textTransform: 'uppercase' as const,
  },
  cardTitle: {
    fontFamily: fontFamilies.title,
    fontSize: fontSizes.analyticsCardTitle,
    fontWeight: fontWeights.bold,
    lineHeight: 1.2,
  },
  cardBody: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.analyticsCardBody,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.caption,
  },
  popoverTitle: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.analyticsPopoverTitle,
    fontWeight: fontWeights.semibold,
    lineHeight: lineHeights.title,
  },
  popoverBody: {
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.analyticsPopoverBody,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.caption,
  },
  bigNumber: {
    fontFamily: fontFamilies.title,
    fontSize: fontSizes.analyticsBigNumber,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.displayTight,
    letterSpacing: letterSpacing.tighter,
  },
  chartDefaults: {
    baseSize: 12,
    titleSize: 14,
    titleWeight: fontWeights.semibold.toString(),
    bodyWeight: fontWeights.regular.toString(),
    tooltipTitleWeight: fontWeights.semibold.toString(),
  },
} as const;

export const typographySpacing = {
  text: {
    xxs: 0.25,
    xs: 0.5,
    sm: 0.75,
    md: 1,
    lg: 1.5,
    xl: 2,
  },
  stack: {
    xs: 0.5,
    sm: 1,
    md: 1.5,
    lg: 2,
    xl: 3,
  },
} as const;

const typographyTokenSet = {
  fontFamilies,
  fontWeights,
  fontSizes,
  lineHeights,
  letterSpacing,
  typographyColors,
  typography,
  semanticTypography,
  analyticsTypography,
  typographySpacing,
} as const;

export default typographyTokenSet;
